import type { RendererAdapterOptions } from './rendererAdapter.js';

/** Optional parity instrumentation; the production adapter contract is unchanged. */
export interface RendererReadback {
  readPixels(): Uint8ClampedArray | Promise<Uint8ClampedArray>;
  readRetainedMembership(): readonly string[] | Promise<readonly string[]>;
  readDiagnostics?(): unknown;
}
const readbackReceiver = Symbol('renderer parity readback receiver');
const canvasReadbacks = new WeakMap<HTMLCanvasElement,RendererReadback>();
type InstrumentedOptions = RendererAdapterOptions & {
  [readbackReceiver]?: (readback: RendererReadback) => void;
};

/** The symbol survives ordinary options forwarding/spreading by factory wrappers. */
export function withRendererReadback(options: RendererAdapterOptions, receive: (readback: RendererReadback) => void): RendererAdapterOptions {
  return { ...options, [readbackReceiver]: receive } as InstrumentedOptions;
}

export function provideRendererReadback(options: RendererAdapterOptions, readback: RendererReadback): void {
  (options as InstrumentedOptions)[readbackReceiver]?.(readback);
}

/** Registers the backend-neutral reader used by native-pixel acceptance captures. */
export function withCanvasRendererReadback(options:RendererAdapterOptions):RendererAdapterOptions {
  return withRendererReadback(options,readback=>canvasReadbacks.set(options.canvas,readback));
}

export async function captureRendererCanvas(canvas:HTMLCanvasElement):Promise<Blob> {
  const readback=canvasReadbacks.get(canvas);if(!readback)throw new Error('renderer readback unavailable');
  const pixels=await readback.readPixels();
  if(pixels.length!==canvas.width*canvas.height*4)throw new Error('renderer readback dimensions do not match canvas');
  const output=document.createElement('canvas');output.width=canvas.width;output.height=canvas.height;
  const context=output.getContext('2d');if(!context)throw new Error('capture canvas unavailable');
  context.putImageData(new ImageData(new Uint8ClampedArray(pixels),canvas.width,canvas.height),0,0);
  return await new Promise<Blob>((resolve,reject)=>output.toBlob(blob=>blob?resolve(blob):reject(new Error('PNG encoding failed')),'image/png'));
}

export function rendererCaptureDiagnostics(canvas:HTMLCanvasElement):unknown {
  return canvasReadbacks.get(canvas)?.readDiagnostics?.() ?? null;
}
